import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, BookOpen, Quote, Sparkles, Eye, Loader2, ExternalLink, Wand2 } from "lucide-react";
import { MarketingNav, MarketingFooter, PipelineBar } from "@/components/marketing/MarketingChrome";
import { SHOWCASE } from "@/data/showcase";
import { Seo } from "@/components/Seo";
import { PdfFlipReader } from "@/components/marketing/PdfFlipReader";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Showcase() {
  const s = SHOWCASE;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reader, setReader] = useState(null); // volume being previewed
  const [cloning, setCloning] = useState(false);

  const startFromThis = async () => {
    if (!user) {
      navigate(`/register?next=${encodeURIComponent(`/showcase/${s.slug}`)}`);
      return;
    }
    setCloning(true);
    try {
      const { data } = await api.post("/projects/from-showcase");
      toast.success("Your edition is ready — start generating!");
      navigate(`/project/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create your edition");
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title={`${s.title} — made with Espresso`}
        description={`${s.subtitle} A ${s.stats[0].value}-page, ${s.stats[1].value}-volume work produced with Espresso.`}
        image={s.og}
        path={`/showcase/${s.slug}`}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="border-b border-border grid-lines">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-14 lg:grid-cols-12 lg:py-20">
          <div className="lg:col-span-7">
            <div className="mb-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              <span className="text-[hsl(var(--matcha))]">{s.ref}</span>
              <span className="h-px flex-1 bg-border" />
              <span>Made with Espresso</span>
            </div>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="showcase-title">
              {s.title}
            </h1>
            <p className="mt-5 max-w-2xl font-display text-xl text-foreground">{s.subtitle}</p>
            <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{s.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#volumes" data-testid="showcase-read-cta">
                <Button size="lg" className="group rounded-sm px-7">
                  <BookOpen size={16} className="mr-2" /> Read the volumes
                </Button>
              </a>
              <Button
                size="lg"
                variant="outline"
                className="group rounded-sm px-7"
                onClick={startFromThis}
                disabled={cloning}
                data-testid="showcase-make-cta"
              >
                {cloning ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wand2 size={16} className="mr-2" />}
                Start from this book
              </Button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-3 rounded-md bg-[hsl(var(--matcha)/0.1)] blur-xl" aria-hidden />
              <img
                src={s.cover}
                alt={`Cover of ${s.title}`}
                className="relative w-full rounded-md border border-border shadow-2xl"
                data-testid="showcase-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px bg-border sm:grid-cols-4">
          {s.stats.map((st) => (
            <div key={st.label} className="bg-card px-6 py-8 text-center" data-testid={`showcase-stat-${st.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-4xl font-bold tracking-tight text-[hsl(var(--teal))]">{st.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{st.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pipeline — how it was made */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--matcha))]">How Espresso produced it</div>
          <PipelineBar steps={s.pipeline} testid="showcase-pipeline" />
          <p className="mt-6 max-w-2xl leading-relaxed text-muted-foreground">
            A single master index of {s.stats[2].value} sections was ingested, decomposed into research questions,
            processed against evidence, reviewed for integrity, then synthesised and cross-linked into finished,
            publication-ready volumes — the same engine you get on every Espresso project.
          </p>
        </div>
      </section>

      {/* The knowledge index */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">The Knowledge Index</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Seventeen sections, one connected body of knowledge.</h2>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {s.sections.map((sec) => (
              <div key={sec.n} className="group bg-card p-6 transition-colors hover:bg-secondary" data-testid={`showcase-section-${sec.n}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--teal))]">{sec.n}</div>
                <h3 className="mt-3 font-display text-lg font-semibold leading-tight tracking-tight">{sec.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{sec.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="border-b border-border bg-secondary/50">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <Quote size={28} className="mx-auto text-[hsl(var(--matcha))]" />
          <blockquote className="mt-5 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl" data-testid="showcase-pullquote">
            “{s.pullQuote}”
          </blockquote>
          <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            From Volume I · Foundations of Human Flourishing
          </div>
        </div>
      </section>

      {/* Volumes */}
      <section id="volumes" className="border-b border-border scroll-mt-20">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--matcha))]">The Volumes</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Read the finished work.</h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">Every volume below was generated and typeset by Espresso. Open any PDF to read it.</p>
          </div>
          <div className="space-y-px overflow-hidden rounded-sm border border-border bg-border">
            {s.volumes.map((v) => (
              <div
                key={v.n}
                data-testid={`showcase-volume-${v.n}`}
                className="flex flex-col gap-3 bg-card p-6 sm:flex-row sm:items-center"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-[hsl(var(--teal))]/40 bg-[hsl(var(--teal)/0.08)] font-display text-lg font-semibold text-[hsl(var(--teal))]">
                  {v.n}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3">
                    <h3 className="font-display text-lg font-semibold leading-tight tracking-tight">{v.title}</h3>
                    <span className="font-mono text-[11px] text-muted-foreground">{v.pages}</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:flex-nowrap">
                  <Button
                    size="sm"
                    className="rounded-sm"
                    onClick={() => setReader(v)}
                    data-testid={`showcase-read-inline-${v.n}`}
                  >
                    <Eye size={14} className="mr-1.5" /> Read inline
                  </Button>
                  <a href={v.file} target="_blank" rel="noopener noreferrer" data-testid={`showcase-open-pdf-${v.n}`}>
                    <Button size="sm" variant="outline" className="rounded-sm">
                      <Download size={13} className="mr-1.5" /> Open PDF
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-16 md:flex-row md:items-center">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--matcha))]">
              <Sparkles size={13} /> Your turn
            </div>
            <h2 className="max-w-xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              A book this large began as a single index. What will you build?
            </h2>
          </div>
          <Button
            size="lg"
            className="rounded-sm px-8"
            onClick={startFromThis}
            disabled={cloning}
            data-testid="showcase-cta-button"
          >
            {cloning ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wand2 size={16} className="mr-2" />}
            Start from this book
          </Button>
        </div>
      </section>

      {/* Inline PDF reader */}
      <Dialog open={!!reader} onOpenChange={(o) => !o && setReader(null)}>
        <DialogContent className="max-w-5xl gap-0 p-0 sm:max-w-5xl" data-testid="showcase-reader-dialog">
          <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b border-border px-5 py-3">
            <DialogTitle className="font-display text-base font-semibold">
              {reader ? `Volume ${reader.n} · ${reader.title}` : ""}
              {reader && <span className="ml-2 font-mono text-[11px] font-normal text-muted-foreground">{reader.pages}</span>}
            </DialogTitle>
            {reader && (
              <a href={reader.file} target="_blank" rel="noopener noreferrer" className="mr-6 shrink-0" data-testid="showcase-reader-newtab">
                <Button size="sm" variant="outline" className="rounded-sm">
                  <ExternalLink size={13} className="mr-1.5" /> Open in new tab
                </Button>
              </a>
            )}
          </DialogHeader>
          {reader && (
            <PdfFlipReader file={reader.file} />
          )}
        </DialogContent>
      </Dialog>

      <MarketingFooter />
    </div>
  );
}
